import { Capacitor } from '@capacitor/core';
import { Filesystem, Encoding } from '@capacitor/filesystem';
import { App } from '@capacitor/app';
import { STORAGE_DIR, STRUCTURE_FILE, aiFileFor, transcriptFileFor } from '@/services/offline/offlinePaths';

// Backend de persistência dos metadados offline (estrutura/grids/enrolls/imageMap
// + cache de IA por LO).
//
// NATIVO: arquivos JSON em Directory.Data — o mesmo sandbox persistente onde o
//   vídeo vive. Sobrevive à eviction do WebKit (que pode apagar localStorage no
//   WKWebView sob pressão de disco) e não tem o teto de ~5MB do localStorage.
// WEB: localStorage (offline é app-only; o caminho web só precisa não quebrar).
//
// Estratégia: snapshot em memória + write-through. Os reads continuam SÍNCRONOS
// (lendo do snapshot), então os ~18 call sites não precisam virar async. Os
// writes mutam o snapshot na hora e disparam persistência assíncrona (debounced
// + atômica) no nativo. `hydrate()` (awaited no boot) carrega o snapshot do
// arquivo antes de qualquer read.

const STORAGE_KEY = 'ead_offline_structure'; // backend web
const FLUSH_DEBOUNCE_MS = 400;

const isNative = Capacitor.isNativePlatform();

let _snapshot = null;          // { enrolls, grids:{[id]:{fullGrid,cachedAt}}, imageMap }
let _dirty = false;
let _flushTimer = null;
let _writeChain = Promise.resolve(); // serializa writes do structure.json

// Acessa o snapshot em memória. No nativo, antes do hydrate() o snapshot é vazio
// (read = cache miss seguro). No web, carrega do localStorage de forma síncrona
// no primeiro acesso (localStorage é síncrono, não precisa de hydrate awaited).
function _data() {
  if (_snapshot) return _snapshot;
  if (!isNative) {
    try {
      _snapshot = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    } catch {
      _snapshot = {};
    }
  } else {
    _snapshot = {};
  }
  return _snapshot;
}

// Escrita durável + atômica: grava no `.tmp`, apaga o destino e renomeia o tmp
// por cima. O rename no mesmo volume é atômico → o arquivo final NUNCA fica
// truncado por um kill no meio da escrita (um writeFile direto, sim — e era o
// que deixava o structure.json corrompido depois de "baixar + fechar o app",
// caindo no JSON.parse falho do hydrate → snapshot vazio → my-courses em branco).
//
// O delete antes do rename é necessário: o `rename`/`move` do @capacitor/filesystem
// v8 (lib IONFILE) NÃO sobrescreve destino existente de forma confiável (resolve
// SEM mover). Com o destino ausente, o rename é confiável nos dois nativos.
// A janela entre delete e rename é coberta pela recuperação do `.tmp` no hydrate.
async function _writeJson(path, data) {
  const tmp = `${path}.tmp`;
  // Retry curto: absorve hiccup transitório de I/O (disco sob pressão, rename).
  for (let attempt = 1; ; attempt++) {
    try {
      await Filesystem.writeFile({
        path: tmp, data, directory: STORAGE_DIR, encoding: Encoding.UTF8, recursive: true
      });
      // Só apaga se existir. Funcionalmente idêntico ao delete incondicional (o
      // .catch já absorvia o "não existe"), mas o plugin NATIVO loga o erro antes
      // de a promise rejeitar — então a primeira escrita de cada JSON cuspia um
      // OS-PLUG-FILE-0008 vermelho no console. Num curso de 20 aulas eram 20
      // erros falsos escondendo os de verdade. Bônus: com o destino ausente,
      // pulamos o delete e encurtamos a janela delete→rename.
      const exists = await Filesystem.stat({ path, directory: STORAGE_DIR }).catch(() => null);
      if (exists) {
        await Filesystem.deleteFile({ path, directory: STORAGE_DIR }).catch(() => {});
      }
      await Filesystem.rename({ from: tmp, to: path, directory: STORAGE_DIR });
      return;
    } catch (e) {
      if (attempt >= 2) throw e;
      await new Promise(r => setTimeout(r, 150));
    }
  }
}

// Lê e parseia um JSON do Filesystem. Retorna null se ausente ou corrompido
// (parse falho) — nunca lança.
async function _readJson(path) {
  try {
    const { data } = await Filesystem.readFile({
      path, directory: STORAGE_DIR, encoding: Encoding.UTF8
    });
    return JSON.parse(data);
  } catch {
    return null;
  }
}

// Persistência write-through. Web: localStorage síncrono. Nativo: marca dirty e
// agenda flush debounced (coalesce de múltiplos writes em rajada — ex. saveGrid
// + saveEnrolls + mergeImageMap no cacheEnrollStructure).
function _persist() {
  if (!isNative) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(_data()));
    } catch (e) {
      console.warn('[offlineStorage] Falha ao salvar (storage cheio?):', e);
    }
    return;
  }
  _dirty = true;
  if (_flushTimer) return;
  _flushTimer = setTimeout(() => {
    _flushTimer = null;
    _flushNow();
  }, FLUSH_DEBOUNCE_MS);
}

function _flushNow() {
  if (!_dirty) return _writeChain;
  _dirty = false;
  const payload = JSON.stringify(_data());
  // Encadeia no _writeChain pra nunca ter dois writes do mesmo arquivo em voo
  // simultâneo (resolve saveGrid + mergeImageMap quase concorrentes).
  _writeChain = _writeChain
    .then(() => _writeJson(STRUCTURE_FILE, payload))
    .catch(e => {
      // Falhou → continua sujo: re-tenta no próximo _persist (mutação) ou no
      // flush() do pause. Sem isso o delta era perdido silenciosamente.
      _dirty = true;
      console.warn('[offlineStorage] Falha ao persistir structure.json:', e?.message || e);
    });
  return _writeChain;
}

// Percorre o objeto recursivamente substituindo campos 'image' e 'logo' pelo mapa local
function applyMapToImages(obj, map) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(item => applyMapToImages(item, map));

  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    if ((key === 'image' || key === 'logo') && typeof value === 'string' && map[value]) {
      result[key] = map[value];
    } else if (value && typeof value === 'object') {
      result[key] = applyMapToImages(value, map);
    } else {
      result[key] = value;
    }
  }
  return result;
}

function _collectUrlsFromGrid(grid) {
  const urls = new Set();
  const add = (obj) => {
    if (obj && obj.image) urls.add(obj.image);
    if (obj && obj.logo) urls.add(obj.logo);
  };
  const addClasses = (classes) => {
    (classes || []).forEach(cls => {
      add(cls);
      (cls.learningObjects || []).forEach(lo => add(lo));
    });
  };
  if (!grid) return [];
  add(grid);
  addClasses(grid.classes);
  (grid.modules || []).forEach(mod => {
    add(mod);
    addClasses(mod.classes);
    (mod.disciplines || []).forEach(disc => addClasses(disc.classes));
  });
  (grid.disciplines || []).forEach(disc => addClasses(disc.classes));
  return [...urls].filter(Boolean);
}

export const offlineStorage = {
  // Carrega o snapshot do disco para a memória. AWAITAR no boot (main.js) antes
  // de qualquer read offline. No-op no web (reads caem no localStorage via _data).
  async hydrate() {
    if (!isNative) return;
    // Lê o arquivo; se faltar/corromper, tenta o `.tmp` — cobre um kill na janela
    // entre o delete e o rename de _writeJson, onde o destino some mas o tmp já
    // tem a versão nova íntegra. Senão (usuário novo), snapshot vazio.
    _snapshot = (await _readJson(STRUCTURE_FILE))
      || (await _readJson(`${STRUCTURE_FILE}.tmp`))
      || {};
  },

  // Força a gravação pendente imediatamente (chamado ao app ir pro background).
  async flush() {
    if (!isNative) return;
    if (_flushTimer) {
      clearTimeout(_flushTimer);
      _flushTimer = null;
    }
    _flushNow();
    await _writeChain;
  },

  saveEnrolls(enrollsData) {
    const data = _data();
    data.enrolls = enrollsData;
    _persist();
  },

  getEnrolls() {
    const data = _data();
    if (!data.enrolls) return null;
    const map = data.imageMap || {};
    return Object.keys(map).length ? applyMapToImages(data.enrolls, map) : data.enrolls;
  },

  saveGrid(enrollId, fullGridData) {
    const data = _data();
    if (!data.grids) data.grids = {};
    data.grids[enrollId] = {
      fullGrid: fullGridData,
      cachedAt: Date.now()
    };
    _persist();
  },

  getGrid(enrollId) {
    const data = _data();
    const entry = (data.grids || {})[enrollId];
    if (!entry) return null;
    const map = data.imageMap || {};
    return Object.keys(map).length ? applyMapToImages(entry.fullGrid, map) : entry.fullGrid;
  },

  hasGrid(enrollId) {
    return Boolean((_data().grids || {})[enrollId]);
  },

  // Configurações de curso (enableIAContent/enableSubtitles/enableChatBot), que
  // vêm de um endpoint próprio e portanto somem offline. Sem cache, o fallback
  // offline assume a flag do ENROLL — e um curso onde o produtor desligou a IA
  // no nível do curso voltava a exibir a aba de IA sem rede.
  //
  // Chave nova no snapshot, sem tocar em enrolls/grids: arquivo antigo
  // simplesmente não tem `courseSettings` e cai no fallback de hoje.
  saveCourseSettings(courseId, settings) {
    if (courseId == null || !settings) return;
    const data = _data();
    if (!data.courseSettings) data.courseSettings = {};
    data.courseSettings[String(courseId)] = settings;
    _persist();
  },

  getCourseSettings(courseId) {
    if (courseId == null) return null;
    return (_data().courseSettings || {})[String(courseId)] || null;
  },

  mergeImageMap(map) {
    const data = _data();
    data.imageMap = { ...(data.imageMap || {}), ...map };
    _persist();
  },

  getImageMap() {
    return _data().imageMap || {};
  },

  // Poda entradas-lixo do imageMap cuja CHAVE é uma URL já-local
  // (_capacitor_file_), resíduo do bug pré-f3f03d4 (re-cache de URL local).
  // As entradas legítimas têm chave remota (http(s)). Idempotente; persiste só
  // se removeu algo.
  pruneLocalImageMapKeys() {
    const map = _data().imageMap;
    if (!map) return;
    let changed = false;
    for (const key of Object.keys(map)) {
      if (key.includes('_capacitor_file_')) {
        delete map[key];
        changed = true;
      }
    }
    if (changed) _persist();
  },

  // Remove o grid cacheado de um enroll e limpa as entradas do imageMap
  // associadas às imagens desse curso. Arquivos no filesystem não são removidos
  // aqui — isso fica para uma fase futura com gerenciamento completo de storage.
  clearEnroll(enrollId) {
    const data = _data();
    const entry = (data.grids || {})[enrollId];

    if (entry && data.imageMap) {
      // Remove do imageMap as URLs que pertencem a este enroll
      const usedUrls = _collectUrlsFromGrid(entry.fullGrid);
      usedUrls.forEach(url => delete data.imageMap[url]);
    }

    if (data.grids) delete data.grids[enrollId];
    _persist();
  },

  // Cache de IA por LO. Vive em offline/<loId>/ai.json (nativo) — apagado de
  // graça no rmdir recursivo da remoção do LO. Web: localStorage.
  async getAI(loId) {
    if (!isNative) {
      try {
        return JSON.parse(localStorage.getItem(`ead_ai_${loId}`) || 'null');
      } catch {
        return null;
      }
    }
    return await _readJson(aiFileFor(loId)); // null se ausente ou corrompido
  },

  // Só existência, sem ler nem parsear: o heal de reconexão consulta isto para
  // cada aula baixada, e um readFile+JSON.parse por aula pesaria à toa num
  // aparelho com dezenas de downloads.
  async hasAI(loId) {
    if (!isNative) return localStorage.getItem(`ead_ai_${loId}`) != null;
    const stat = await Filesystem.stat({ path: aiFileFor(loId), directory: STORAGE_DIR }).catch(() => null);
    return Boolean(stat);
  },

  async saveAI(loId, aiContent) {
    if (!isNative) {
      try {
        localStorage.setItem(`ead_ai_${loId}`, JSON.stringify(aiContent));
      } catch (e) {
        console.warn('[offlineStorage] Falha ao salvar IA (web):', e);
      }
      return;
    }
    try {
      // Escrita única por LO (no download), sem throttle.
      await _writeJson(aiFileFor(loId), JSON.stringify(aiContent));
    } catch (e) {
      console.warn('[offlineStorage] Falha ao salvar ai.json:', e?.message || e);
    }
  },

  // Cache da transcrição por LO (offline/<loId>/transcript.json). Pré-buscado no
  // download e injetado no módulo `transcript` do coremedia pelo
  // loadOfflineTranscript (StudentClass) — offline o bundle manda subtitles: []
  // e a aba nunca apareceria sozinha.
  async getTranscript(loId) {
    if (!isNative) {
      try {
        return JSON.parse(localStorage.getItem(`ead_transcript_${loId}`) || 'null');
      } catch {
        return null;
      }
    }
    return await _readJson(transcriptFileFor(loId));
  },

  async saveTranscript(loId, transcript) {
    if (!isNative) {
      try {
        localStorage.setItem(`ead_transcript_${loId}`, JSON.stringify(transcript));
      } catch (e) {
        console.warn('[offlineStorage] Falha ao salvar transcript (web):', e);
      }
      return;
    }
    try {
      await _writeJson(transcriptFileFor(loId), JSON.stringify(transcript));
    } catch (e) {
      console.warn('[offlineStorage] Falha ao salvar transcript.json:', e?.message || e);
    }
  }
};

// Flush garantido ao ir pro background — minimiza a janela de perda do último
// write debounced num kill/swipe. Só no nativo.
if (isNative) {
  App.addListener('pause', () => { offlineStorage.flush(); });
}
